import { useMutation } from "@apollo/client";
import { useToast } from "@chakra-ui/react";
import { UPLOAD_UNENCRYPTED_FILE } from "../queries";
import { buildIPFSUrl } from "../utils/ipfs";

/**
 * Custom hook to handle profile photo uploads to IPFS.
 *
 * Returns a callback that accepts a React file-input change event, uploads
 * the first file via the `UPLOAD_UNENCRYPTED_FILE` mutation, and returns
 * the resulting gateway URL (built from the IPFS hash) on success.
 *
 * Toasts on success and failure. Returns `null` on error so the caller
 * can decide whether to update form state.
 */
export const useFileUpload = () => {
    const toast = useToast();
    const [uploadFile] = useMutation(UPLOAD_UNENCRYPTED_FILE);

    const handlePhotoUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ): Promise<string | null> => {
        if (!e.target.files?.length) return null;

        try {
            const file = e.target.files[0];
            const { data } = await uploadFile({
                variables: { file },
                context: {
                    headers: {
                        "apollo-require-preflight": "true",
                        "x-apollo-operation-name": "UploadUnencryptedFile",
                    },
                },
            });

            const hash = data.uploadUnencryptedFile;
            const newPhotoUrl = buildIPFSUrl(hash);

            toast({
                title: "Photo uploaded successfully",
                status: "success",
                duration: 3000,
            });

            return newPhotoUrl;
        } catch (error) {
            toast({
                title: "Upload failed",
                description: error instanceof Error ? error.message : "Unknown error occurred",
                status: "error",
                duration: 5000,
            });
            return null;
        }
    };

    return { handlePhotoUpload };
};
